import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import apiUrls from "../../../api";

export interface Available360Scene {
  id: number;
  name: string;
  parcoursId: number;
  parcoursName: string;
  sceneType: "image360" | "video360";
  thumbnailUrl?: string;
}

interface UseAvailable360ScenesReturn {
  scenes: Available360Scene[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

interface SessionInfo {
  sessionId: number;
  parcoursId: number;
  parcoursName: string;
}

interface ApiSceneResponse {
  scenes: Array<{
    id: number;
    sceneName: string;
    media?: {
      sceneType?: string;
      url?: string;
    };
  }>;
}

/**
 * Hook to fetch available 360° scenes across all parcours for heatmap comparison
 * Returns scenes that have image360 or video360 content
 *
 * @param sessions - Array of session info with parcours context
 * @param enabled - Whether to fetch data
 */
export function useAvailable360Scenes(
  sessions: SessionInfo[],
  enabled = true,
): UseAvailable360ScenesReturn {
  const [scenes, setScenes] = useState<Available360Scene[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchScenes = useCallback(async () => {
    if (!enabled || sessions.length === 0) {
      setScenes([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const allScenes: Available360Scene[] = [];
      const fetchedParcoursIds = new Set<number>();

      // Get one session per parcours to fetch its scenes
      const sessionsToFetch = sessions.filter((session) => {
        if (fetchedParcoursIds.has(session.parcoursId)) {
          return false;
        }
        fetchedParcoursIds.add(session.parcoursId);
        return true;
      });

      for (const session of sessionsToFetch) {
        try {
          // Use the gazeHeatmapData endpoint with sessionId to get scenes
          const response = await axios.get<ApiSceneResponse>(
            `${apiUrls.stats.gazeHeatmapData}?sessionId=${session.sessionId}`,
          );

          if (response.data?.scenes) {
            for (const scene of response.data.scenes) {
              // Only include 360° scenes
              const sceneType = scene.media?.sceneType;
              if (sceneType === "image360" || sceneType === "video360") {
                // Check if we already have this scene
                if (!allScenes.some((s) => s.id === scene.id)) {
                  allScenes.push({
                    id: scene.id,
                    name: scene.sceneName,
                    parcoursId: session.parcoursId,
                    parcoursName: session.parcoursName,
                    sceneType: sceneType as "image360" | "video360",
                    thumbnailUrl: scene.media?.url,
                  });
                }
              }
            }
          }
        } catch {
          // Continue with other sessions if one fails
          console.warn(`Failed to fetch scenes for session ${session.sessionId}`);
        }
      }

      setScenes(allScenes);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to fetch 360° scenes"));
    } finally {
      setIsLoading(false);
    }
  }, [sessions, enabled]);

  useEffect(() => {
    fetchScenes();
  }, [fetchScenes]);

  return { scenes, isLoading, error, refetch: fetchScenes };
}
